import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePdf, ArrowClockwise, Trash, ArrowUp, ArrowDown, ArrowCounterClockwise, CircleNotch, FloppyDisk } from "@phosphor-icons/react";

export default function Studio() {
  const flags = useFeatures();
  const navigate = useNavigate();
  const [pdfs, setPdfs] = useState([]);
  const [docId, setDocId] = useState(null);
  const [info, setInfo] = useState(null);
  const [pages, setPages] = useState([]);
  const [mergeId, setMergeId] = useState("none");
  const [compress, setCompress] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/documents", { params: { doc_type: "pdf", limit: 200 } }).then((r) => setPdfs(r.data)).catch(() => {});
  }, []);

  const loadDoc = useCallback((id) => {
    setDocId(id); setInfo(null); setPages([]); setName("");
    api.get(`/pdf/${id}/info`).then(({ data }) => {
      setInfo(data);
      setPages(Array.from({ length: data.pages }, (_, i) => ({ index: i, rotation: 0, deleted: false })));
      setName(`${data.name} (edited)`);
    }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  const patch = (i, p) => setPages((prev) => prev.map((pg, idx) => (idx === i ? { ...pg, ...p } : pg)));
  const move = (i, dir) => setPages((prev) => {
    const j = i + dir; if (j < 0 || j >= prev.length) return prev;
    const n = [...prev]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const save = async () => {
    const kept = pages.filter((p) => !p.deleted);
    if (!kept.length) { toast.info("Keep at least one page."); return; }
    const order = kept.map((p) => p.index);
    const rotations = {};
    kept.forEach((p, j) => { if (p.rotation) rotations[String(j)] = p.rotation; });
    setBusy(true);
    try {
      const { data } = await api.post(`/pdf/${docId}/apply`, {
        order, rotations, compress,
        merge_doc_id: mergeId !== "none" ? mergeId : null,
        name: name.trim() || null,
      });
      toast.success(`Saved as v${data.version}`);
      navigate(`/dashboard/vault?open=${data.doc_id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!flags.PDF_STUDIO_ENABLED) {
    return (
      <div className="max-w-xl">
        <div className="flex items-center gap-2 mb-1"><FilePdf size={26} className="text-primary" weight="duotone" /><h1 className="text-3xl font-bold tracking-tight">PDF Studio</h1></div>
        <div className="mt-6 rounded-2xl border border-dashed border-white/20 bg-white p-8 text-center" data-testid="studio-disabled"><p className="text-white/85">PDF Studio is currently turned off for this workspace.</p></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl" data-testid="studio-page">
      <div className="flex items-center gap-2 mb-1"><FilePdf size={26} className="text-primary" weight="duotone" /><h1 className="text-3xl font-bold tracking-tight">PDF Studio</h1></div>
      <p className="text-white/70 mt-1 mb-6 text-sm">Reorder, rotate, delete, split, merge and compress — saved as a new version, your original stays untouched.</p>

      <div className="rounded-2xl border border-white/15 bg-white p-4 mb-5">
        <Label className="text-xs text-white/60">Choose a PDF from your vault</Label>
        <Select value={docId || undefined} onValueChange={loadDoc}>
          <SelectTrigger className="mt-1 max-w-md" data-testid="studio-doc-select"><SelectValue placeholder="Select a PDF…" /></SelectTrigger>
          <SelectContent>{pdfs.map((d) => <SelectItem key={d.doc_id} value={d.doc_id} data-testid={`studio-doc-${d.doc_id}`}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {info && (
        <>
          <div className="rounded-2xl border border-white/15 bg-white p-4 mb-5">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="studio-pages">
              {pages.map((p, i) => (
                <div key={p.index} className={`relative rounded-xl overflow-hidden border-2 bg-black/40 ${p.deleted ? "border-red-500/50 opacity-40" : "border-white/10"}`} data-testid={`studio-page-${i}`}>
                  <div className="aspect-[3/4] overflow-hidden grid place-items-center bg-white">
                    <img src={`${API}/pdf/${docId}/page/${p.index}`} alt={`Page ${p.index + 1}`} className="w-full h-full object-contain" style={{ transform: `rotate(${p.rotation}deg)` }} />
                  </div>
                  <span className="absolute top-1 left-1 text-[10px] bg-black/70 text-white rounded px-1.5 py-0.5">{i + 1}</span>
                  <div className="absolute bottom-1 right-1 flex gap-1">
                    <button onClick={() => move(i, -1)} className="w-6 h-6 grid place-items-center rounded bg-black/70 text-white hover:bg-primary hover:text-[#20242a]" data-testid={`studio-up-${i}`}><ArrowUp size={12} /></button>
                    <button onClick={() => move(i, 1)} className="w-6 h-6 grid place-items-center rounded bg-black/70 text-white hover:bg-primary hover:text-[#20242a]" data-testid={`studio-down-${i}`}><ArrowDown size={12} /></button>
                    <button onClick={() => patch(i, { rotation: (p.rotation + 90) % 360 })} className="w-6 h-6 grid place-items-center rounded bg-black/70 text-white hover:bg-primary hover:text-[#20242a]" data-testid={`studio-rotate-${i}`}><ArrowClockwise size={12} /></button>
                    <button onClick={() => patch(i, { deleted: !p.deleted })} className={`w-6 h-6 grid place-items-center rounded text-white ${p.deleted ? "bg-emerald-600" : "bg-black/70 hover:bg-red-500"}`} data-testid={`studio-delete-${i}`}>{p.deleted ? <ArrowCounterClockwise size={12} /> : <Trash size={12} />}</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/15 bg-white p-4 flex flex-wrap items-end gap-4" data-testid="studio-savebar">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-white/60">New document name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-white/5 border-white/20 text-white" data-testid="studio-name" />
            </div>
            <div className="w-[200px]">
              <Label className="text-xs text-white/60">Append (merge) a PDF</Label>
              <Select value={mergeId} onValueChange={setMergeId}>
                <SelectTrigger className="mt-1" data-testid="studio-merge-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {pdfs.filter((d) => d.doc_id !== docId).map((d) => <SelectItem key={d.doc_id} value={d.doc_id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/80 pb-2"><Switch checked={compress} onCheckedChange={setCompress} data-testid="studio-compress" /> Compress</label>
            <Button onClick={save} disabled={busy} className="rounded-full px-6" data-testid="studio-save">
              {busy ? <><CircleNotch size={18} className="mr-1.5 animate-spin" /> Saving…</> : <><FloppyDisk size={18} className="mr-1.5" /> Save as new version</>}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
